import {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {trpc, createMutationErrorHandler} from '@common-web/lib/trpc';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Textarea} from '@common-web/components/ui/textarea';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {Alert, AlertDescription, AlertTitle} from '@common-web/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {toast} from '@common-web/hooks/use-toast';
import {SCOPE_LABELS, SCOPE_OPTIONS} from '@common/permissions';
import {ArrowLeft, AlertTriangle, Save, ShieldCheck, Trash2, Info} from 'lucide-react';
import {PermissionStatusBadge} from '@internal/components/roles-permissions/PermissionStatusBadge';
import type {Permission} from '@internal/components/roles-permissions/types';

export function PermissionDetail() {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();

  const {data, isLoading, error, refetch} = trpc.internal.permissions.get.useQuery(
    {id: id!},
    {enabled: !!id},
  );

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState<Permission['scope']>('INTERNAL');

  useEffect(() => {
    if (!data) return;
    setName(data.name);
    setDescription(data.description ?? '');
    setScope(data.scope);
  }, [data]);

  const utils = trpc.useUtils();

  const updateMutation = trpc.internal.permissions.update.useMutation({
    onSuccess: async () => {
      await Promise.all([utils.internal.permissions.list.invalidate(), refetch()]);
      toast({title: 'Permission updated'});
    },
    onError: createMutationErrorHandler('Failed to update permission'),
  });

  const deleteMutation = trpc.internal.permissions.delete.useMutation({
    onSuccess: async () => {
      await utils.internal.permissions.list.invalidate();
      toast({title: 'Permission deleted'});
      navigate('/permissions');
    },
    onError: createMutationErrorHandler('Failed to delete permission'),
  });

  const removeDeprecatedMutation = trpc.internal.permissions.removeDeprecated.useMutation({
    onSuccess: async () => {
      await utils.internal.permissions.list.invalidate();
      toast({title: 'Permission removed'});
      navigate('/permissions');
    },
    onError: createMutationErrorHandler('Failed to remove permission'),
  });

  if (isLoading) {
    return (
      <div className='max-w-3xl mx-auto p-8 space-y-4'>
        <Skeleton className='h-8 w-1/2' />
        <Skeleton className='h-32 w-full' />
        <Skeleton className='h-32 w-full' />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className='max-w-3xl mx-auto p-8'>
        <Alert variant='destructive'>
          <AlertTriangle className='h-4 w-4' />
          <AlertTitle>Permission not found</AlertTitle>
          <AlertDescription>
            {error?.message ?? 'This permission may have been removed.'}
          </AlertDescription>
        </Alert>
        <Button variant='outline' className='mt-4' onClick={() => navigate('/permissions')}>
          <ArrowLeft className='h-4 w-4 mr-2' />
          Back to permissions
        </Button>
      </div>
    );
  }

  const isCode = data.source === 'CODE';
  const isDeprecated = data.isDeprecated;

  const handleSave = () => {
    updateMutation.mutate({
      id: data.id,
      name: name.trim(),
      description: description.trim() ? description.trim() : null,
      scope,
    });
  };

  const handleDelete = () => {
    if (!confirm(`Delete "${data.name}"? This cannot be undone.`)) return;
    deleteMutation.mutate({id: data.id});
  };

  const handleRemoveDeprecated = () => {
    if (
      !confirm(
        `Permanently remove "${data.name}"? Any roles that include this permission will lose this capability.`,
      )
    ) {
      return;
    }
    removeDeprecatedMutation.mutate({id: data.id});
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='max-w-3xl mx-auto p-8 space-y-4'>
        <Button variant='ghost' onClick={() => navigate('/permissions')}>
          <ArrowLeft className='h-4 w-4 mr-2' />
          Back to permissions
        </Button>

        {/* Summary card — what this permission is and does */}
        <Card className='border-gray-200 shadow-sm'>
          <CardHeader className='border-b bg-gray-50'>
            <div className='flex items-start justify-between'>
              <div className='space-y-1'>
                <CardTitle className='text-2xl font-bold text-brand-navy flex items-center gap-2'>
                  <ShieldCheck className='h-5 w-5' />
                  {data.name}
                </CardTitle>
                <div className='flex items-center gap-2'>
                  <Badge variant='outline' className='font-mono text-[10px] tracking-wider'>
                    {SCOPE_LABELS[data.scope]?.split(' (')[0]}
                  </Badge>
                  <PermissionStatusBadge permission={data} />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className='pt-5'>
            {data.description ? (
              <p className='text-sm text-brand-navy leading-relaxed'>{data.description}</p>
            ) : (
              <p className='text-sm text-brand-gray italic'>
                No description yet. Add one below so other admins understand what this permission
                controls.
              </p>
            )}
          </CardContent>
        </Card>

        {isDeprecated && (
          <Alert variant='destructive'>
            <AlertTriangle className='h-4 w-4' />
            <AlertTitle>No longer in use</AlertTitle>
            <AlertDescription>
              This permission is no longer enforced by the application. You can safely remove it once
              any roles that include it have been updated.
            </AlertDescription>
          </Alert>
        )}

        {/* Settings card — editable fields */}
        <Card className='border-gray-200 shadow-sm'>
          <CardHeader className='border-b bg-gray-50'>
            <CardTitle className='text-lg font-semibold text-brand-navy'>Settings</CardTitle>
          </CardHeader>
          <CardContent className='space-y-5 pt-6'>
            <div className='grid gap-1.5'>
              <Label htmlFor='permission-name'>Display name</Label>
              <Input
                id='permission-name'
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={updateMutation.isPending}
              />
            </div>

            <div className='grid gap-1.5'>
              <Label htmlFor='permission-description'>Description</Label>
              <Textarea
                id='permission-description'
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                disabled={updateMutation.isPending}
                placeholder='Describe what this permission allows a user to do.'
              />
            </div>

            <div className='grid gap-1.5'>
              <Label htmlFor='permission-scope'>Who is this for?</Label>
              <Select
                value={scope}
                onValueChange={(value) => setScope(value as Permission['scope'])}
                disabled={updateMutation.isPending}
              >
                <SelectTrigger id='permission-scope' className='w-64'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCOPE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {SCOPE_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className='text-xs text-brand-gray'>
                Determines which types of roles can include this permission.
              </p>
            </div>

            <div className='flex items-center justify-between pt-4 border-t'>
              <div className='flex gap-2'>
                {!isCode && (
                  <Button
                    variant='destructive'
                    size='sm'
                    onClick={handleDelete}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className='h-4 w-4 mr-2' />
                    Delete permission
                  </Button>
                )}
                {isCode && isDeprecated && (
                  <Button
                    variant='destructive'
                    size='sm'
                    onClick={handleRemoveDeprecated}
                    disabled={removeDeprecatedMutation.isPending}
                  >
                    <Trash2 className='h-4 w-4 mr-2' />
                    Remove permission
                  </Button>
                )}
              </div>
              <Button onClick={handleSave} disabled={updateMutation.isPending}>
                <Save className='h-4 w-4 mr-2' />
                Save changes
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Technical details — collapsed to the bottom for developers */}
        <Card className='border-gray-200 shadow-sm'>
          <CardContent className='py-4'>
            <div className='flex items-center gap-2 text-xs text-brand-gray'>
              <Info className='h-3.5 w-3.5 shrink-0' />
              <span>
                Permission key: <code className='font-mono bg-gray-100 px-1 rounded-sm'>{data.key}</code>
                {isCode && ' · Managed by the application'}
                {!isCode && ' · Created manually'}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
